import React, { useState, useRef, useEffect } from "react";
import styles from "./CodeValidation.module.scss";
import Button from "../Forms/Button/Button";
import { motion } from "framer-motion";
import { fireAnalytics } from "../../../utils/firebase.js";
import { CodeValidationProps } from "../../../types/register.ts";
import { Clipboard, AlertCircle, X, RefreshCw } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { resendVerificationCode, validateVerificationCode, clearRegistrationFlow } from "../../../store/actions";

const CodeValidation: React.FC<CodeValidationProps> = ({ 
  email, 
  onChangeEmail,
  isLoading, 
  errorMessage,
  specialMessage
}) => {
  const dispatch = useDispatch();
  const isResendingCode = useSelector((state: any) => state.register?.isResendingCode || false);
  const resendCodeError = useSelector((state: any) => state.register?.resendCodeError || null);
  const isCodeValidating = useSelector((state: any) => state.register?.isCodeValidating || false);
  const codeValidationError = useSelector((state: any) => state.register?.codeValidationError || null);
  const codeValidationData = useSelector((state: any) => state.register?.codeValidationData || null);


  
  const [code, setCode] = useState<string>("");
  const [showResend, setShowResend] = useState<boolean>(false);
  const [countDown, setCountDown] = useState<number>(60);
  const [pasteError, setPasteError] = useState<string>("");
  const [showErrorToast, setShowErrorToast] = useState<boolean>(false);
  const [showSpecialToast, setShowSpecialToast] = useState<boolean>(true);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleCodeChange = (index: number, value: string): void => {
    if (!/^\d*$/.test(value)) return;
    
    const newCode = code.substring(0, index) + value + code.substring(index + 1);
    setCode(newCode);
    
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").slice(0, 6);
    
    if (/^\d{6}$/.test(pastedData)) {
      setCode(pastedData);
      setPasteError("");
      
      // Focus last input after paste
      setTimeout(() => {
        inputRefs.current[5]?.focus();
      }, 100);
    }
  };

  const handlePasteButton = async (): Promise<void> => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      const pastedData = clipboardText.slice(0, 6);
      
      if (/^\d{6}$/.test(pastedData)) {
        setCode(pastedData);
        setPasteError("");
        
        // Focus last input after paste
        setTimeout(() => {
          inputRefs.current[5]?.focus();
        }, 100);
        
        fireAnalytics("codigo_pegado_portapapeles", { category: "usuario" });
      } else {
        setPasteError("El contenido del portapapeles no es un código válido de 6 dígitos");
        setTimeout(() => setPasteError(""), 3000);
      }
    } catch (error) {
      setPasteError("No se pudo acceder al portapapeles. Intenta pegar manualmente (Ctrl+V)");
      setTimeout(() => setPasteError(""), 3000);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (code.length === 6) {
      fireAnalytics("codigo_validacion_enviado", { category: "usuario" });
      
      try {
        // Usar la acción de Redux para validar el código
        await dispatch(validateVerificationCode(email, code) as any);
        
        
        setShowErrorToast(false);
      } catch (error) {
        console.error("❌ Error en CodeValidation handleSubmit:", error);
        
        // Mostrar el toast de error
        setShowErrorToast(true);
        
        // Auto-ocultar después de 5 segundos
        setTimeout(() => {
          setShowErrorToast(false);
        }, 5000);
      }
    }
  };

  const handleResend = async (): Promise<void> => {
    try {
      // Usar la acción de Redux para reenviar el código
      await dispatch(resendVerificationCode(email) as any);
      
      // Si llegamos aquí, el reenvío fue exitoso
      setCountDown(60);
      setShowResend(false);
      
      // Ya no llamamos al callback del componente padre porque ya manejamos el resend aquí
      // if (onResendCode) {
      //   await onResendCode();
      // }
    } catch (error) {
      console.error("❌ Error en CodeValidation handleResend:", error);
      
      // Mostrar el toast de error
      setShowErrorToast(true);
      
      // Auto-ocultar después de 5 segundos
      setTimeout(() => {
        setShowErrorToast(false);
      }, 5000);
    }
  };

  useEffect(() => {
    if (countDown > 0) {
      const timer = setTimeout(() => setCountDown(countDown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setShowResend(true);
    }
  }, [countDown]);

  // Marcar cuando el componente se monta y limpiar errores anteriores
  useEffect(() => {
    setIsMounted(true);
    // Limpiar errores anteriores del flujo de registro para evitar toasts automáticos
    dispatch(clearRegistrationFlow() as any);
  }, []);

  useEffect(() => {
    if (code.length === 6) {
      handleSubmit();
    }
  }, [code]);

  // Mostrar toast de error cuando hay un error
  useEffect(() => {
    // Solo mostrar toast si hay un error Y el componente ya está montado (no al montar inicialmente)
    if ((errorMessage || resendCodeError || codeValidationError) && isMounted) {
      setShowErrorToast(true);
      const timer = setTimeout(() => {
        setShowErrorToast(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage, resendCodeError, codeValidationError, isMounted]);

  // Detectar cuando la validación del código sea exitosa
  useEffect(() => {
    if (codeValidationData) {

      setCode("");
      setShowErrorToast(false);
    }
  }, [codeValidationData, code]);

  const handleCloseToast = (): void => {
    setShowErrorToast(false);
  };

  const handleCloseSpecialToast = (): void => {
    setShowSpecialToast(false);
  };

  const containerVariant = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94],
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariant = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  };

  return (
    <motion.div
      variants={containerVariant}
      initial="hidden"
      animate="visible"
      className={styles.container}
    >
      <motion.div variants={itemVariant} className={styles.content}>
        <motion.p variants={itemVariant} className={styles.subtitle}>
          Te enviamos un código de 6 dígitos a <strong>{email}</strong>
        </motion.p>
        
        {specialMessage && showSpecialToast && (
          <motion.div 
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            className={styles.specialToast}
          >
            <div className={styles.specialToast_content}>
              <span className={styles.specialToast_text}>{specialMessage}</span>
              <button 
                onClick={handleCloseSpecialToast}
                className={styles.specialToast_close}
                type="button"
              >
                <X size={16} />
              </button>
            </div>
          </motion.div>
        )}

        <motion.div variants={itemVariant} className={styles.codeContainer}>
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric" // Teclado numérico en mobile
              pattern="[0-9]*" // Solo números en iOS
              maxLength={1}
              value={code[index] || ""}
              onChange={(e) => handleCodeChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              className={styles.codeInput}
              disabled={isLoading || isCodeValidating}
              autoComplete="one-time-code"
              placeholder="-"
            />
          ))}
        </motion.div>

        {/* Botón de pegar */}
        <motion.div variants={itemVariant} className={styles.pasteSection}>
          <button
            onClick={handlePasteButton}
            disabled={isLoading || isCodeValidating}
            className={styles.pasteButton}
            type="button"
          >
            <Clipboard size={18} className={styles.pasteIcon} />
            <span>Pegar código</span>
          </button>
          
          {pasteError && (
            <motion.p 
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              className={styles.pasteError}
            >
              {pasteError}
            </motion.p>
          )}
        </motion.div>



        {/* Toast de error que no ocupa espacio */}
        {(errorMessage || resendCodeError || codeValidationError) && showErrorToast && (
          <motion.div 
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            className={styles.errorToast}
          >
            <div className={styles.errorToast_content}>
              <AlertCircle size={20} className={styles.errorToast_icon} />
              <span className={styles.errorToast_text}>{codeValidationError || resendCodeError || errorMessage}</span>
              <button 
                onClick={handleCloseToast}
                className={styles.errorToast_close}
                type="button"
              >
                <X size={16} />
              </button>
            </div>
          </motion.div>
        )}

        <motion.div variants={itemVariant} className={styles.actions}>
          {showResend ? (
            <Button
              onClick={handleResend}
              disabled={isLoading || isResendingCode || isCodeValidating}
              className={styles.resendButton}
              colored
              bordered={false}
              link={false}
              typebtn="primary"
            >
              <RefreshCw size={18} className={styles.resendIcon} />
              {isResendingCode ? "Reenviando..." : "Reenviar código"}
            </Button>
          ) : (
            <p className={styles.countdown}>
              <RefreshCw size={18} className={styles.resendIcon} />
              Reenviar en {countDown}s
            </p>
          )}
        </motion.div>

        <motion.div variants={itemVariant} className={styles.help}>
          <p>¿No recibiste el código?</p>
          <ul>
            <li>Revisa tu carpeta de spam</li>
            <li>Verifica que el email sea correcto o <a onClick={onChangeEmail} className={styles.helpLink}>cambia el email</a></li>
          </ul>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default CodeValidation;
